/**
 * "Usually around 07:00."
 *
 * The owner asked for templates to "predefine suggested time for day where instantiated". The
 * column for it — `preferred_windows` — has existed since Phase 4, is API-settable, exported,
 * imported and honoured. There was simply no way to set one without curl, so nobody ever had.
 *
 * A named part of the day rather than two clock fields. The stored shape is a list of `{from,
 * to}` ranges and the API will take any of them, but somebody writing down when they usually go
 * to the gym is not choosing 06:15–08:45 — they are choosing "morning". Four buttons express
 * every answer anybody gave in the persona round, and a template written by curl with some
 * other range keeps it: the control shows "Custom" and leaves it alone unless changed.
 */

export interface Window {
  from: string;
  to: string;
}

const WINDOW_PRESETS: { key: string; label: string; window: Window }[] = [
  { key: 'morning', label: 'Morning', window: { from: '06:00', to: '11:00' } },
  { key: 'midday', label: 'Midday', window: { from: '11:00', to: '14:00' } },
  { key: 'afternoon', label: 'Afternoon', window: { from: '14:00', to: '18:00' } },
  { key: 'evening', label: 'Evening', window: { from: '18:00', to: '22:00' } },
];

/** What the forms start from. Anything unrecognised is 'custom', which is left untouched. */
export function windowKey(value: unknown): string {
  const list = Array.isArray(value) ? (value as Window[]) : [];
  if (list.length === 0) return '';
  const first = list[0];
  if (!first) return '';
  const match = WINDOW_PRESETS.find(
    (p) => p.window.from === first.from && p.window.to === first.to,
  );
  return match ? match.key : 'custom';
}

/** What goes back to the API. `null` for 'custom' means "do not send it", not "clear it". */
export function windowValue(key: string): Window[] | null {
  if (key === 'custom') return null;
  if (key === '') return [];
  const preset = WINDOW_PRESETS.find((p) => p.key === key);
  return preset ? [preset.window] : [];
}

export function PreferredWindowField({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  id: string;
}) {
  return (
    <label>
      Usually around
      <select
        id={id}
        value={value}
        aria-label="Usually around"
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Any time</option>
        {WINDOW_PRESETS.map((preset) => (
          <option key={preset.key} value={preset.key}>
            {preset.label} ({preset.window.from}–{preset.window.to})
          </option>
        ))}
        {value === 'custom' && <option value="custom">Custom</option>}
      </select>
    </label>
  );
}
